import { client } from '../client';
import { GET_SURVEILLANCE_ROUTE_PRESETS } from '../queries/surveillanceRoutePresets';
import { SurveillanceRoutePreset } from '../../types/surveillanceRoute';

/**
 * Tope de espera al revalidar el catálogo de plantillas.
 *
 * Abrir el asistente es camino crítico: el operario está al borde de la
 * calzada. Con cobertura la consulta son unas pocas filas y vuelve muy por
 * debajo de esto; sin ella, o con una red que se arrastra, se deja de esperar
 * y se sirve lo último descargado. La consulta lenta sigue viva y escribe en
 * la caché cuando llegue, así que la siguiente entrada ya sale al día.
 */
export const PRESETS_REFRESH_TIMEOUT_MS = 2000;

/**
 * Catálogo de plantillas para los asistentes de recorrido (crear y editar).
 *
 * 🔴 Aquí NO vale `cache-first`, y es lo que distingue a este catálogo del
 * resto de datos maestros: lo escribe la OFICINA y quien lo consume es la
 * TABLET, que por diseño no puede escribirlo. O sea que el consumidor nunca
 * invalida su propia caché — y dentro del APK el WebView no se cierra nunca,
 * así que el almacén de sesión sobrevive días y una plantilla dada de alta hoy
 * no llegaría jamás a quien sale a ruta. (Las carreteras o las concesiones sí
 * pueden ir `cache-first`: no cambian.)
 *
 * Se revalida contra el servidor con tope de tiempo y se cae a lo último
 * descargado. Con cobertura llega el catálogo de hoy; sin ella, el de la
 * última vez — que es la promesa que hace que esto exista: no teclear el PK
 * con guantes y sin señal.
 *
 * `errorPolicy: 'ignore'` en las dos consultas: sin plantillas el asistente es
 * el de siempre, así que un fallo de esta consulta no puede dejar sin
 * asistente a quien no usa la función.
 */
export const readSurveillanceRoutePresets = async (): Promise<
  SurveillanceRoutePreset[]
> => {
  const fromCache = async () => {
    const cached = await client
      .query({
        query: GET_SURVEILLANCE_ROUTE_PRESETS,
        variables: { onlyActive: true },
        fetchPolicy: 'cache-only',
        errorPolicy: 'ignore',
      })
      .catch(() => null);
    return cached?.data?.surveillanceRoutePresets ?? [];
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const fresh = await Promise.race([
    client
      .query({
        query: GET_SURVEILLANCE_ROUTE_PRESETS,
        variables: { onlyActive: true },
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      })
      .catch(() => null),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), PRESETS_REFRESH_TIMEOUT_MS);
    }),
  ]);
  if (timer) clearTimeout(timer);

  // Un backend anterior a esta query responde con error y `data` vacío: eso no
  // es «no hay plantillas», es «no me he enterado» → se conserva lo que ya
  // hubiera en la caché en vez de vaciar el desplegable.
  return fresh?.data?.surveillanceRoutePresets ?? fromCache();
};

export default readSurveillanceRoutePresets;
